import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
const HERO = "/images/saint-sylvestre-hero.jpg";

export const Route = createFileRoute("/dj-saint-sylvestre")({
  head: () => ({
    meta: [
      { title: "DJ Saint-Sylvestre & Nouvel An Vaucluse | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("DJ pour votre réveillon du 31 décembre en Provence : sono, éclairage, décompte de minuit et dancefloor jusqu'à 5 h. Avignon, Cavaillon, Aix et Luberon.")
      },
      { property: "og:title", content: "DJ Saint-Sylvestre & Nouvel An — Provence | DJ Val" },
      {
        property: "og:description",
        content: metaDesc("Réveillon clé en main : trame musicale, décompte minuit, effets lumière, ambiance dancefloor jusqu'au petit matin. Formules 890 € → 2 490 €.")
      },
      { property: "og:url", content: "https://dj-val.fr/dj-saint-sylvestre" },
      { property: "og:image", content: "https://dj-val.fr/images/saint-sylvestre-hero.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://dj-val.fr/images/saint-sylvestre-hero.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/dj-saint-sylvestre" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Saint-Sylvestre & Jour de l'An en Provence",
          serviceType: "Animation musicale réveillon Nouvel An",
          description:
            "Animation musicale complète pour votre soirée du 31 décembre en Provence : sono, éclairage, décompte minuit, dancefloor jusqu'au petit matin. 20 ans d'expérience, 40+ réveillons.",
          areaServed: ["Vaucluse", "Bouches-du-Rhône", "Gard", "Avignon", "Cavaillon", "Aix-en-Provence", "Luberon", "Alpilles"],
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "890",
            highPrice: "2490",
            offerCount: "3",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Jusqu'à quelle heure animez-vous un réveillon du 31 décembre ?", acceptedAnswer: { "@type": "Answer", text: "Jusqu'à 4 h ou 5 h du matin selon la salle. Les formules Saint-Sylvestre incluent par défaut une plage horaire étendue." } },
            { "@type": "Question", name: "Intervenez-vous à Avignon et Aix-en-Provence pour le réveillon ?", acceptedAnswer: { "@type": "Answer", text: "Oui, DJ Val intervient dans tout le Vaucluse, les Bouches-du-Rhône, le Gard et le sud de la Drôme. Déplacement offert dans un rayon de 80 km autour de Cavaillon." } },
            { "@type": "Question", name: "Quel est le tarif d'un DJ pour la Saint-Sylvestre ?", acceptedAnswer: { "@type": "Answer", text: "Formules à partir de 890 € (5 h, sono + lumière) jusqu'à 2 490 € (nuit complète avec effets spéciaux, étincelles froides, canon CO₂)." } },
            { "@type": "Question", name: "Peut-on réserver son DJ Nouvel An à la dernière minute ?", acceptedAnswer: { "@type": "Answer", text: "Contactez-moi rapidement : il reste parfois une date en novembre-décembre, mais l'idéal est de bloquer votre 31 décembre dès l'été précédent." } },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const FORMULAS = [
  {
    name: "Essentielle",
    price: "890 €",
    hours: "5 heures — 21 h → 2 h",
    features: [
      "Sono RCF ou Electro-Voice",
      "Éclairage piste (4 PARs LED)",
      "Micro HF pour discours",
      "Décompte minuit orchestré",
      "Playlist personnalisée",
    ],
    highlight: false,
  },
  {
    name: "Signature",
    price: "1 490 €",
    hours: "Nuit complète — 20 h → 4 h",
    features: [
      "Sono renforcée + subwoofer",
      "Éclairage architectural + 4 lyres LED",
      "Machine à fumée basse",
      "Trame cocktail → dîner → dancefloor",
      "Coordination maître de cérémonie",
      "Étincelles froides à minuit",
    ],
    highlight: true,
  },
  {
    name: "Prestige",
    price: "2 490 €",
    hours: "Nuit XL — 19 h → 5 h",
    features: [
      "Configuration club (2 sub + 4 tops)",
      "Show lumière programmé sur mesure",
      "Canon à CO₂ + étincelles froides",
      "Effet confettis à minuit",
      "Second technicien sur place",
      "Enregistrement audio du décompte",
    ],
    highlight: false,
  },
];

const TIMELINE = [
  { time: "19h30", label: "Cocktail", desc: "Ambiance jazz-lounge, easy-listening, volume feutré pour laisser les invités se rencontrer." },
  { time: "21h00", label: "Dîner", desc: "Bossa nova, French touch mid-tempo, hits acoustiques. Micro HF pour vos discours." },
  { time: "23h30", label: "Ouverture piste", desc: "Montée en énergie contrôlée : classiques disco, French, hits actuels pour préparer le décompte." },
  { time: "23h58", label: "Décompte minuit", desc: "Coordination millimétrée : Auld Lang Syne, Champagne Supernova, effets lumière et étincelles synchronisés." },
  { time: "00h05", label: "Peak time", desc: "Le dancefloor explose : house, hits internationaux, remix multi-générations. Personne ne s'assoit." },
  { time: "04h00", label: "Closing", desc: "Descente maîtrisée : slows cultes, closing anthem, on laisse les invités partir avec des étoiles plein la tête." },
];

function Page() {
  useReveal();

  return (
    <div className="bg-background text-foreground">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={HERO}
          alt="Décompte de minuit lors d'un réveillon du Nouvel An animé par DJ Val en Provence"
          width={1920}
          height={1280}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-background/60 to-transparent" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-40 pb-20 grid lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-8 sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Réveillon 31 décembre · Nouvel An</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl leading-[1.02] mt-6 text-foreground">
              La nuit
              <span className="block italic text-[var(--gold)]">la plus attendue</span>
              de l'année.
            </h1>
            <span className="gold-rule my-8" />
            <p className="max-w-xl text-base md:text-lg font-light leading-relaxed text-muted-foreground">
              DJ pour votre Saint-Sylvestre en Provence. Sono, éclairage, décompte de minuit orchestré, dancefloor
              jusqu'au petit matin — un réveillon clé en main, sans stress d'organisation.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contactez-nous"
                onClick={() => trackEvent("cta_click", { cta_location: "saint_sylvestre_hero", cta_label: "reserver_reveillon" })}
                className="bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
              >
                Réserver mon réveillon
              </Link>
              <a
                href="tel:0767908667"
                onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "saint_sylvestre_hero" })}
                className="border border-foreground/30 text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
                style={{ fontFamily: "Arial, sans-serif", letterSpacing: "0.1em" }}
              >
                07 67 90 86 67
              </a>
            </div>
          </div>

          <aside className="lg:col-span-4 sr" data-reveal>
            <div className="border border-[var(--gold-line)] bg-background/70 backdrop-blur-md p-8">
              <p className="eyebrow text-[var(--gold)]">Dates 2026</p>
              <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed">
                Les réveillons se bloquent <strong className="text-foreground">6 à 8 mois à l'avance</strong>.
                Pour le 31 décembre 2026, contactez-moi avant l'été.
              </p>
              <dl className="mt-6 divide-y divide-[var(--gold-line)]">
                <div className="py-3 flex justify-between"><dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Zone</dt><dd className="text-sm text-foreground">Provence · 80 km</dd></div>
                <div className="py-3 flex justify-between"><dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Formule</dt><dd className="text-sm text-foreground">dès 890 €</dd></div>
                <div className="py-3 flex justify-between"><dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Réponse</dt><dd className="text-sm text-foreground">sous 24 h</dd></div>
              </dl>
            </div>
          </aside>
        </div>
      </section>
      <ReviewsProof />

      {/* PROMISE */}
      <section className="py-24 px-6 max-w-4xl mx-auto text-center">
        <p className="eyebrow text-[var(--gold)] sr" data-reveal>Notre promesse</p>
        <h2 className="font-serif-display text-3xl md:text-5xl text-foreground mt-6 leading-tight sr" data-reveal>
          Un réveillon <span className="italic">inoubliable</span>,
          <br /> sans une seconde de stress.
        </h2>
        <span className="gold-rule mx-auto my-8" />
        <p className="text-base md:text-lg font-light leading-relaxed text-muted-foreground sr" data-reveal>
          Le 31 décembre, tout doit être parfait : le décompte à minuit, la musique qui fait exploser la piste,
          l'énergie qui tient jusqu'à 5 h du matin. Avec <strong className="text-foreground">20 ans d'expérience</strong> et
          plus de <strong className="text-foreground">40 réveillons</strong> animés en Vaucluse et en Provence, DJ Val gère
          toute la trame sonore et lumineuse de votre soirée : cocktail, dîner, décompte, dancefloor, closing.
        </p>
      </section>

      {/* TIMELINE */}
      <section className="border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Trame d'une soirée type</p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-foreground mt-4">
              La chorégraphie d'un Nouvel An réussi
            </h2>
            <span className="gold-rule my-6" />
            <p className="max-w-2xl text-sm text-muted-foreground font-light leading-relaxed">
              Chaque moment a sa signature musicale et lumineuse. Rien n'est improvisé, tout est chronométré au quart d'heure près.
            </p>
          </div>

          <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {TIMELINE.map((t, i) => (
              <li key={t.time} className="sr relative pl-8 border-l border-[var(--gold-line)]" style={{ transitionDelay: `${i * 70}ms` }} data-reveal>
                <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-[var(--gold)]" />
                <p className="text-2xl text-[var(--gold)] tabular-nums" style={{ fontFamily: "Arial, sans-serif" }}>{t.time}</p>
                <h3 className="font-serif-display text-xl text-foreground mt-2">{t.label}</h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed mt-2">{t.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FORMULAS */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Formules Saint-Sylvestre</p>
            <h2 className="font-serif-display text-3xl md:text-5xl text-foreground mt-4">
              Trois formules, <span className="italic">une exigence</span>
            </h2>
            <span className="gold-rule mx-auto my-6" />
            <p className="max-w-2xl mx-auto text-sm text-muted-foreground font-light leading-relaxed">
              Toutes incluent la livraison, l'installation, le démontage et une réunion de préparation en visio.
              Personnalisation illimitée du set-list.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {FORMULAS.map((f, i) => (
              <div
                key={f.name}
                className={`sr relative flex flex-col p-8 border ${f.highlight ? "border-[var(--gold)] bg-card shadow-[0_0_60px_-20px_var(--gold)]" : "border-[var(--gold-line)] bg-card"}`}
                style={{ transitionDelay: `${i * 80}ms` }}
                data-reveal
              >
                {f.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--gold)] text-[var(--ink)] px-4 py-1 text-[10px] uppercase tracking-[0.25em] font-semibold">
                    Le plus choisi
                  </span>
                )}
                <p className="eyebrow text-[var(--gold)]">{f.name}</p>
                <p className="text-5xl text-foreground mt-4 tabular-nums" style={{ fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{f.price}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-2">{f.hours}</p>
                <span className="gold-rule !w-8 my-6" />
                <ul className="space-y-3 text-sm font-light text-muted-foreground leading-relaxed flex-1">
                  {f.features.map((feat) => (
                    <li key={feat} className="flex gap-3">
                      <span className="text-[var(--gold)] mt-1">◆</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contactez-nous"
                  onClick={() => trackEvent("cta_click", { cta_location: "saint_sylvestre_formula", cta_label: f.name })}
                  className={`mt-8 text-center px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                    f.highlight ? "bg-[var(--gold)] text-[var(--ink)] hover:brightness-110" : "border border-foreground/20 text-foreground hover:border-[var(--gold)] hover:text-[var(--gold)]"
                  }`}
                >
                  Choisir {f.name}
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground font-light">
            Léger supplément nuit du 31 (+15 %) inclus dans les tarifs affichés. Aucun frais caché.
          </p>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="eyebrow text-[var(--gold)] sr" data-reveal>Ils ont dansé jusqu'à 5 h</p>
          <blockquote className="font-serif-display text-2xl md:text-3xl text-foreground italic leading-relaxed mt-6 sr" data-reveal>
            « On avait peur d'un réveillon trop calme. Val a lu la salle en 10 minutes, orchestré le décompte comme dans un film,
            et personne n'a quitté la piste avant 5 h. Le meilleur choix de notre soirée. »
          </blockquote>
          <span className="gold-rule mx-auto my-8" />
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground sr" data-reveal>
            Sophie &amp; Marc — Réveillon 60 pers., Mas de la Rose (Alpilles)
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-foreground mt-4">Tout ce que vous voulez savoir</h2>
            <span className="gold-rule mx-auto my-6" />
          </div>

          <div className="mt-10 space-y-3">
            {[
              { q: "Jusqu'à quelle heure pouvez-vous animer un réveillon ?", a: "Généralement jusqu'à 4 h ou 5 h du matin, en fonction de la salle et du permis d'exploitation. Les tarifs Saint-Sylvestre incluent une plage horaire étendue par défaut." },
              { q: "Intervenez-vous à Avignon et Aix-en-Provence pour le 31 décembre ?", a: "Oui, j'interviens dans tout le Vaucluse, les Bouches-du-Rhône, le Gard et le sud de la Drôme. Aucun frais de déplacement dans un rayon de 80 km autour de Cavaillon." },
              { q: "Le tarif Saint-Sylvestre est-il majoré ?", a: "Un léger supplément nuit du 31 (+15 % environ) est déjà inclus dans les tarifs affichés pour couvrir la plage horaire longue et la disponibilité premium. Aucun frais caché." },
              { q: "Puis-je réserver à la dernière minute ?", a: "Contactez-moi : selon les années il reste parfois une date disponible en novembre-décembre, mais l'idéal est de réserver dès l'été précédent." },
              { q: "Gérez-vous aussi les effets spéciaux (étincelles, CO₂) ?", a: "Oui, étincelles froides à minuit incluses en formule Signature et Prestige, canon à CO₂ et confettis en Prestige. Tous les effets sont conformes ERP et sécurisés." },
              { q: "Un acompte est-il demandé ?", a: "30 % à la signature du contrat pour bloquer la date, solde le jour J. Devis gratuit et sans engagement." },
            ].map((f, i) => (
              <details key={i} className="group border border-[var(--gold-line)] bg-card open:bg-secondary/40 sr" style={{ transitionDelay: `${i * 40}ms` }} data-reveal>
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="font-serif-display text-base md:text-lg text-foreground">{f.q}</span>
                  <span className="text-[var(--gold)] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-foreground text-background py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--gold)]">Dates 2026 · Il reste peu de places</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-6">
            On bloque votre <span className="italic text-[var(--gold)]">31 décembre</span> ?
          </h2>
          <span className="gold-rule mx-auto my-6" />
          <p className="text-sm text-background/70 font-light leading-relaxed">
            Réponse sous 24 h, devis gratuit et sans engagement. Un échange visio de 30 minutes offert pour construire votre trame musicale.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contactez-nous"
              onClick={() => trackEvent("cta_click", { cta_location: "saint_sylvestre_final", cta_label: "devis_31_decembre" })}
              className="bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Demander un devis 31 décembre
            </Link>
            <a
              href="mailto:info.djval@gmail.com"
              onClick={() => trackEvent("contact_click", { contact_method: "email", cta_location: "saint_sylvestre_final" })}
              className="border border-background/30 px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
            >
              info.djval@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
